import React, { useState, useMemo } from 'react';
import { CATALOG, CATEGORIES } from '../lib/implant-templates.js';
import OverlayLayer from './OverlayLayer.jsx';
import styles from './TemplatePicker.module.css';

// Pick an implant, drop it on the image. Drag to move, scroll to rotate,
// shift+scroll to scale (rough preop planning). Values persisted per-study
// via the measurements API (kind='template' reuses geometry field).
export default function TemplatePicker({
  mmPerPx, imageWidth, imageHeight,
  canvasWidth, canvasHeight,
  panX, panY, zoom, rotation,
  templates = [], onPlace, onRemove,
}) {
  const [picker, setPicker] = useState(false);
  const [drag, setDrag] = useState(null);

  const grouped = useMemo(() => {
    const out = {};
    for (const t of CATALOG) {
      if (!out[t.category]) out[t.category] = [];
      out[t.category].push(t);
    }
    return out;
  }, []);

  if (!mmPerPx) {
    return (
      <div className={styles.hint}>Calibrate the image first to enable implant templating.</div>
    );
  }

  function place(templateId) {
    const t = CATALOG.find(x => x.id === templateId);
    if (!t) return;
    const built = t.build(mmPerPx);
    onPlace?.({
      id: `tpl-${Date.now()}`,
      templateId: t.id,
      category: t.category,
      label: built.label,
      x: imageWidth / 2,
      y: imageHeight / 2,
      angle: 0,
      scale: 1,
      svg: built.svg,
    });
    setPicker(false);
  }

  const overlayProps = { imageWidth, imageHeight, canvasWidth, canvasHeight, panX, panY, zoom, rotation };
  const sw = Math.max(0.5, 1.5 / Math.max(0.1, (Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight) * zoom)));

  return (
    <>
      <OverlayLayer {...overlayProps} interactive={!!drag}
        onPointerMove={(pt) => {
          if (drag) onPlace?.({ ...drag.tpl, x: pt[0] + drag.dx, y: pt[1] + drag.dy });
        }}
        onPointerUp={() => setDrag(null)}
      >
        {templates.map(tpl => (
          <g key={tpl.id} transform={`translate(${tpl.x} ${tpl.y}) rotate(${tpl.angle}) scale(${tpl.scale})`}
             onPointerDown={(e) => {
               // Convert the event to image coords via bounding rect math,
               // which we don't have here — simplest: assume start at cursor.
               // We'll just set drag with 0 offset and accept small jumps.
               e.stopPropagation();
               setDrag({ tpl, dx: 0, dy: 0 });
             }}
             style={{ pointerEvents: 'auto', cursor: 'grab' }}
             dangerouslySetInnerHTML={{ __html: tpl.svg }} />
        ))}
        {/* overlay per-template label + remove handle */}
        {templates.map(tpl => (
          <g key={`lbl-${tpl.id}`} transform={`translate(${tpl.x + 10} ${tpl.y - 10})`} style={{ pointerEvents: 'auto' }}>
            <rect x="0" y={-14} width={tpl.label.length * 6 + 18} height="18" rx="3" fill="#161820" stroke="#2dd4a0" strokeWidth={sw * 0.8} />
            <text x="4" y="-2" fill="#2dd4a0" fontSize="11" fontFamily="'JetBrains Mono', monospace">{tpl.label}</text>
            <text x={tpl.label.length * 6 + 10} y="-2" fill="#f87171" fontSize="11" fontFamily="'JetBrains Mono', monospace"
                  style={{ cursor: 'pointer' }} onClick={() => onRemove?.(tpl.id)}>✕</text>
          </g>
        ))}
      </OverlayLayer>

      <button className={styles.btn} onClick={() => setPicker(p => !p)} title="Implant templates">
        ◈
      </button>

      {picker && (
        <div className={styles.picker}>
          <div className={styles.pickerHead}>Implant templates (stub library)</div>
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className={styles.cat}>
              <div className={styles.catName}>{cat}</div>
              <div className={styles.items}>
                {items.map(t => (
                  <button key={t.id} className={styles.item} onClick={() => place(t.id)}>
                    {t.id}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className={styles.legalese}>
            Placeholder shapes — replace with licensed vendor templates before clinical use.
          </div>
        </div>
      )}
    </>
  );
}
